import type { Apis, SessionService } from '@efdigital/ll-addon-auth';
import { default as BaseCoachService } from '@efdigital/ll-addon-coaching/services/coach';
import type { CoachingSession, CoachProfile } from '@efdigital/ll-addon-data';
import type {
  CoachCoacheePii,
  CoachPostAttendanceMarkRequestBody,
} from '@efdigital/ll-cdk-coaching-sessions-api-types';
import config from '@efdigital/ll-xceed-v2/config/environment';
import { service } from '@ember/service';
import type { Store } from '@warp-drive/core';

import { queryCoachProfileByUserId } from '../data/builders/coaching/coach-profiles.ts';
import {
  buildCoachCancelSessionRequest,
  buildCoachGetCoacheesRequest,
  buildCoachMarkAttendanceRequest,
  parseCoachGetCoacheesResponse,
  parseCoachMarkAttendanceResponse,
} from '../data/builders/coaching/sessions-as-coach.ts';

const apis: Apis = config['ll-addon-auth-apis'];

export default class CoachService extends BaseCoachService {
  apis = apis;
  @service declare store: Store;
  @service declare session: SessionService;
  #cachedCoachProfile: { exists: boolean; profile?: CoachProfile } | null =
    null;
  #cachedCoachees: CoachCoacheePii[] | undefined = undefined;

  async validateCodeFromQueryParams(): Promise<boolean> {
    const url = new URL(window.location.href);
    const code = url.searchParams.get('code');
    if (!code) {
      return false;
    }

    try {
      await this.validateCode(code);
      return true;
    } catch (error) {
      console.error('Failed to validate Cronofy code from query params', error);
      return false;
    }
  }

  async #ensureCachedCoachProfile() {
    if (this.#cachedCoachProfile) {
      return this.#cachedCoachProfile;
    }

    const response = await this.store.request(
      queryCoachProfileByUserId(this.session.cached.userId!, 'published')
    );
    const hasProfile = response.content.data.length > 0;
    this.#cachedCoachProfile = {
      exists: hasProfile,
      profile: hasProfile ? response.content.data[0] : undefined,
    };
    return this.#cachedCoachProfile;
  }

  async isCoach() {
    return (await this.#ensureCachedCoachProfile()).exists;
  }

  async getPublishedProfile() {
    const profile = (await this.#ensureCachedCoachProfile()).profile;
    if (!profile) {
      throw new Error('Coach profile not found');
    }
    return profile;
  }

  async #ensureCachedCoachees() {
    if (this.#cachedCoachees) {
      return this.#cachedCoachees;
    }

    const response = await fetch(
      await buildCoachGetCoacheesRequest(this.apis, this.session)
    );
    if (!response.ok) {
      throw new Error('Failed to fetch coachees');
    }
    const data = await parseCoachGetCoacheesResponse(response);
    return data.coachees;
  }

  async getCoachees(): Promise<CoachCoacheePii[]> {
    return await this.#ensureCachedCoachees();
  }

  async lookupCoachee(userId: string): Promise<CoachCoacheePii | undefined> {
    if (this.#cachedCoachees) {
      const alreadyLoaded = this.#cachedCoachees.find(
        (coachee) => coachee.userId === userId
      );
      if (alreadyLoaded) {
        return alreadyLoaded;
      }
    }
    return (await this.#ensureCachedCoachees()).find(
      (coachee) => coachee.userId === userId
    );
  }

  async cancelSession(coachingSession: CoachingSession, cancelReason: string) {
    const query = await buildCoachCancelSessionRequest(
      this.apis,
      this.session,
      coachingSession,
      { cancelReason }
    );

    const response = await fetch(query);
    if (!response.ok) {
      throw new Error('Failed to cancel session');
    }
  }

  async markAttendance(
    coachingSession: CoachingSession,
    body: CoachPostAttendanceMarkRequestBody
  ) {
    const query = await buildCoachMarkAttendanceRequest(
      this.apis,
      this.session,
      coachingSession,
      body
    );

    const response = await fetch(query);
    if (!response.ok) {
      throw new Error('Failed to mark attendance');
    }

    return await parseCoachMarkAttendanceResponse(response);
  }
}
